import React, { useState } from "react";
import { Button, Flex, HStack, IconButton } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

interface PaginationProps {
    totalResults: number;
    pageSize: number;
    currentPage?: number;
    onPageChange?: (pageIndex: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ totalResults, pageSize, currentPage = 0, onPageChange }) => {
    const [pageIndex, setPageIndex] = useState(currentPage);
    const totalPages = Math.ceil(totalResults / pageSize);

    // Sync internal state with external currentPage prop
    React.useEffect(() => {
        setPageIndex(currentPage);
    }, [currentPage]);

    const goToPage = (newIndex: number) => {
        setPageIndex(newIndex);
        onPageChange?.(newIndex);
    };

    const renderPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxVisible = 3; // Maximum middle pages visible

        if (totalPages <= maxVisible + 2) {
            for (let i = 0; i < totalPages; i++) pages.push(i);
        } else {
            pages.push(0); // Always show page 1

            if (pageIndex > 2) pages.push("...");

            let start = Math.max(1, pageIndex - 1);
            let end = Math.min(totalPages - 2, pageIndex + 1);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            if (pageIndex < totalPages - 3) pages.push("...");
            pages.push(totalPages - 1); // Always show last page
        }

        return pages;
    };

    return (
        <Flex justifyContent="center" align="center" mt="4">
            <IconButton
                aria-label="Previous Page"
                icon={<ChevronLeftIcon />}
                onClick={() => goToPage(Math.max(pageIndex - 1, 0))}
                isDisabled={pageIndex === 0}
            />

            <HStack mx="4">
                {renderPageNumbers().map((page, index) => (
                    <Button
                        key={index}
                        onClick={() => typeof page === "number" && goToPage(page)}
                        bg={pageIndex === page ? "brand.500" : "white"}
                        variant={pageIndex === page ? "solid" : "outline"}
                        isDisabled={typeof page !== "number"}
                        color={pageIndex === page ? "white" : "brand.500"}
                        rounded={"full"}
                    >
                        {typeof page === "number" ? page + 1 : "..."}
                    </Button>
                ))}
            </HStack>

            <IconButton
                aria-label="Next Page"
                icon={<ChevronRightIcon />}
                onClick={() => goToPage(Math.min(pageIndex + 1, totalPages - 1))}
                isDisabled={pageIndex >= totalPages - 1}
            />
        </Flex>
    );
};

export default Pagination;